import type { AuctisyncSupabase } from './client.js';

/**
 * Finding a lot in a sale that is too big to scroll.
 *
 * 812 lots is more than a chip row can carry, so search is a real screen and
 * filtering is a sheet. The rules live here rather than in either surface's
 * component, because "closing soonest" and "next hour" have to mean the same
 * thing on a phone and on a laptop — a bidder who filters to the last hour on
 * one and sees a different set on the other has no reason to trust either.
 *
 * PURE, AND OVER LOTS ALREADY LOADED. `fetchAuctionLots` fetches the whole sale
 * with no limit, so the list is already in memory; filtering it again on the
 * server would be a second round trip for data the app is holding.
 *
 * ⚠️ SEPARATE CONCERN, NOT SOLVED HERE: that unbounded fetch is itself worth
 * revisiting for a very large sale on a phone. This module does not make it
 * worse, and pretending to fix it with a filter would hide it.
 */

export type LotSort = 'closing_soonest' | 'lot_number' | 'price_low_high' | 'most_bids';
export type ClosingWindow = 'any' | 'next_30_min' | 'next_hour' | 'today' | 'this_sale';

/**
 * "Closing soon" means the next 30 minutes — on every surface.
 *
 * Browse's rail says "Closing soon · Next 30 min" in the design, and its chip
 * of the same name must show exactly the lots the rail does. Both the app and
 * the website read this constant through `searchLots`, so there is one number
 * to change and no way for a rail and a chip to disagree about what "soon" is.
 */
export const CLOSING_SOON_MS = 30 * 60_000;

export interface LotFilters {
  sort: LotSort;
  /** Category ids. Empty means every category. */
  categoryIds: string[];
  /** Only lots with no reserve. */
  noReserveOnly: boolean;
  closing: ClosingWindow;
  /** Inclusive bounds on the current bid, in cents. Null means unbounded. */
  minCents: number | null;
  maxCents: number | null;
}

export const NO_FILTERS: LotFilters = {
  sort: 'lot_number',
  categoryIds: [],
  noReserveOnly: false,
  closing: 'any',
  minCents: null,
  maxCents: null,
};

/** The fields search and filtering read. Kept narrow so any row shape can be passed. */
export interface SearchableLot {
  id: string;
  title: string | null;
  description: string | null;
  lot_number: number | null;
  category_id: string | null;
  current_bid_cents: number | null;
  starting_bid_cents: number | null;
  bid_count: number | null;
  has_reserve: boolean | null;
  ends_at: string | null;
}

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** The price a bidder is filtering on: what it costs now, or what it opens at. */
function priceOf(lot: SearchableLot): number {
  return lot.current_bid_cents ?? lot.starting_bid_cents ?? 0;
}

function endsAtMs(lot: SearchableLot): number | null {
  if (lot.ends_at === null) return null;
  const ms = Date.parse(lot.ends_at);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Free-text match across title and description.
 *
 * EVERY WORD MUST MATCH, in any order and anywhere. "milwaukee drill" should
 * find "Drill, Milwaukee M18" — a substring match on the whole phrase would
 * not, and that is the single most common way a search feels broken.
 */
export function matchesQuery(lot: SearchableLot, query: string): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = `${lot.title ?? ''} ${lot.description ?? ''}`.toLowerCase();
  return terms.every((t) => haystack.includes(t));
}

/**
 * How many filters are active.
 *
 * The sort is NOT counted: every list is sorted somehow, so counting it would
 * show "1 filter" on a screen where nothing has been filtered. Price counts
 * once whether one bound is set or both — it is one decision the bidder made.
 */
export function activeFilterCount(filters: LotFilters): number {
  return (
    (filters.categoryIds.length > 0 ? 1 : 0) +
    (filters.noReserveOnly ? 1 : 0) +
    (filters.closing !== 'any' ? 1 : 0) +
    (filters.minCents !== null || filters.maxCents !== null ? 1 : 0)
  );
}

/**
 * Apply search and filters, then sort.
 *
 * `nowMs` is injected rather than read from the clock: the closing windows are
 * relative, and a function that reads `Date.now()` internally cannot be tested
 * against a boundary.
 */
export function searchLots<T extends SearchableLot>(
  lots: readonly T[],
  query: string,
  filters: LotFilters,
  nowMs: number,
): T[] {
  const out = lots.filter((lot) => {
    if (!matchesQuery(lot, query)) return false;

    if (filters.categoryIds.length > 0) {
      if (lot.category_id === null || !filters.categoryIds.includes(lot.category_id)) return false;
    }

    // `has_reserve` null means unknown, and unknown is not the same as "no
    // reserve" — offering a lot as no-reserve when nobody recorded whether it
    // has one is a claim about the terms of a sale.
    if (filters.noReserveOnly && lot.has_reserve !== false) return false;

    if (filters.closing !== 'any' && filters.closing !== 'this_sale') {
      const ends = endsAtMs(lot);
      // A lot with no close time cannot be inside a closing window. Including
      // it would put lots that may never close at the top of "next hour".
      if (ends === null) return false;
      const remaining = ends - nowMs;
      if (remaining < 0) return false;
      if (filters.closing === 'next_30_min' && remaining > CLOSING_SOON_MS) return false;
      if (filters.closing === 'next_hour' && remaining > HOUR_MS) return false;
      if (filters.closing === 'today' && remaining > DAY_MS) return false;
    }

    const price = priceOf(lot);
    if (filters.minCents !== null && price < filters.minCents) return false;
    if (filters.maxCents !== null && price > filters.maxCents) return false;

    return true;
  });

  return sortLots(out, filters.sort, nowMs);
}

/**
 * The lots closing in the next 30 minutes, soonest first.
 *
 * Expressed THROUGH `searchLots` rather than beside it, so the rail on Browse
 * and the "Closing soon" chip cannot drift: both are this one filter.
 */
export function closingSoon<T extends SearchableLot>(lots: readonly T[], nowMs: number): T[] {
  return searchLots(
    lots,
    '',
    { ...NO_FILTERS, closing: 'next_30_min', sort: 'closing_soonest' },
    nowMs,
  );
}

function sortLots<T extends SearchableLot>(lots: T[], sort: LotSort, nowMs: number): T[] {
  const copy = [...lots];
  switch (sort) {
    case 'lot_number':
      // Nulls last: an unnumbered lot at the top of a numbered list reads as a
      // fault in the list rather than a lot without a number.
      return copy.sort((a, b) => nullsLast(a.lot_number, b.lot_number));
    case 'price_low_high':
      return copy.sort((a, b) => priceOf(a) - priceOf(b));
    case 'most_bids':
      return copy.sort((a, b) => (b.bid_count ?? 0) - (a.bid_count ?? 0));
    case 'closing_soonest':
      /*
       * 🔴 LOTS THAT HAVE ALREADY CLOSED SORT LAST, not first.
       *
       * A plain ascending sort on `ends_at` puts every finished lot at the top,
       * because the past is earlier than the future — so a sale that is half
       * over opens with a screen of lots nobody can bid on. "Closing soonest"
       * is a question about what is still open; a closed lot has no answer to
       * it and belongs behind everything that does.
       *
       * Within each group the order is still ascending, so the finished lots
       * read as a sale history rather than a shuffled pile.
       */
      return copy.sort((a, b) => {
        const ea = endsAtMs(a);
        const eb = endsAtMs(b);
        const aOver = ea !== null && ea < nowMs;
        const bOver = eb !== null && eb < nowMs;
        if (aOver !== bOver) return aOver ? 1 : -1;
        return nullsLast(ea, eb);
      });
  }
}

/** Ascending, with nulls pushed to the end rather than sorting as zero. */
function nullsLast(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

/**
 * Parse a price a person typed into cents.
 *
 * Returns null for empty OR unusable input, so a typo becomes "no bound"
 * rather than a bound of NaN — which would filter every lot out and look like
 * the sale had emptied.
 */
export function parsePriceBound(raw: string): number | null {
  const trimmed = raw.trim().replace(/[$,\s]/g, '');
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}

/**
 * The categories actually present in a set of lots, named.
 *
 * Only the ones IN THIS SALE. Listing a house's whole category tree would offer
 * a bidder filters that return nothing — the opposite of what the match count
 * on the apply button is for.
 *
 * Fails to an empty list rather than an error: the filter sheet hides the
 * category section when there is nothing to show, so a category read that
 * fails costs one filter rather than the whole sheet.
 */
export async function fetchLotCategories(
  supabase: AuctisyncSupabase,
  lots: readonly SearchableLot[],
): Promise<{ id: string; name: string }[]> {
  const ids = [
    ...new Set(lots.map((l) => l.category_id).filter((id): id is string => id !== null)),
  ];
  if (ids.length === 0) return [];

  const { data, error } = await supabase
    .from('item_categories')
    .select('id, name')
    .in('id', ids)
    .order('sort_order', { ascending: true, nullsFirst: false });

  if (error) return [];

  return (data ?? [])
    .map((raw) => {
      const row = raw as { id?: unknown; name?: unknown };
      return {
        id: typeof row.id === 'string' ? row.id : '',
        name: typeof row.name === 'string' ? row.name : '',
      };
    })
    .filter((c) => c.id !== '' && c.name !== '');
}
